import moment from 'moment';
import { SelectOption } from '../components/select/Select';
import { STANDARD_DATE_FORMAT } from '../constants/dateConstants';

// The backend stores/assumes UTC; anchor dates to UTC here so results don't shift with the viewer's timezone.
export type DateInput = string | number | Date;

export const getUtcDayStart = (value: DateInput): number => moment.utc(value).startOf('day').valueOf();

export const getUtcDayEnd = (value: DateInput): number => moment.utc(value).endOf('day').valueOf();

// True when the UTC instant `value` falls within the inclusive day range [fromDate, toDate].
export const isWithinUtcDayRange = (value: DateInput, fromDate: DateInput, toDate: DateInput): boolean =>
    moment.utc(value).isBetween(moment.utc(fromDate), moment.utc(toDate), 'day', '[]');

export const getQuarterDates = (startDate: string, endDate: string) => {
    const quarterStartDates = [];
    const quarterEndDates = [];
    const startMDate = moment(startDate);
    const endMDate = moment(endDate);
    const endOfQuarter = Math.floor((endMDate.month() + 3) / 3);
    const endOfQuarterDate = endMDate.clone().quarter(endOfQuarter).endOf('quarter');

    for (let date = startMDate.clone().startOf('quarter'); date.isBefore(endMDate); date.add(3, 'months').startOf('quarter')) {
        quarterStartDates.push(date.valueOf());
    }

    for (let date = startMDate.clone().endOf('quarter'); date.isSameOrBefore(endOfQuarterDate); date.add(3, 'months').endOf('quarter')) {
        quarterEndDates.push(date.valueOf());
    }

    const startDateOptions = quarterStartDates.reverse().map(date => ({
        label: 'Q' + moment(date).format('Q YYYY'),
        value: String(date),
    }));
    const endDateOptions = quarterEndDates.reverse().map(date => ({
        label: 'Q' + moment(date).format('Q YYYY'),
        value: String(date),
    }));

    return {
        startDateOptions,
        endDateOptions,
    };
};

export const getQuarterDateRange = (quarter: string | null): { quarterStart: string; quarterEnd: string } | null => {
    if (!quarter) {
        return null;
    }

    let quarterMoment;
    if (quarter.includes('-')) {
        quarterMoment = moment(quarter);
    } else {
        quarterMoment = moment(Number(quarter));
    }

    const quarterStart = quarterMoment.format('YYYY-MM-DD');
    const quarterEnd = quarterMoment.endOf('quarter').format('YYYY-MM-DD');
    return { quarterStart, quarterEnd };
};

export const getDateQuarterSelectOption = (dateTime: number, useEndDate = false): SelectOption => {
    const momentDate = moment(dateTime);

    return {
        label: 'Q' + momentDate.format('Q YYYY'),
        value: (useEndDate ? momentDate.endOf('quarter') : momentDate.startOf('quarter')).format('YYYY-MM-DD'),
    };
};

export const formatRelativeDate = (dateString: string): string => {
    const announcementDate = moment(dateString);
    const now = moment();
    const diffDays = Math.ceil(now.diff(announcementDate, 'days', true));

    if (diffDays === 0) {
        return 'Today';
    } else if (diffDays === 1) {
        return 'Yesterday';
    } else if (diffDays < 7) {
        return `${diffDays} days ago`;
    } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
        const years = Math.floor(diffDays / 365);
        return `${years} year${years > 1 ? 's' : ''} ago`;
    }
};

export const formatStandardDate = (value: DateInput | null | undefined): string => {
    return value === null || value === undefined ? '' : moment.utc(value).format(STANDARD_DATE_FORMAT);
};

// Compact 'DDMMYY' UTC date, e.g. for filenames.
export const formatCompactDate = (value: DateInput | null | undefined): string => {
    return value === null || value === undefined ? '' : moment.utc(value).format('DDMMYY');
};
